import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, PhoneIcon, MailIcon, CalendarIcon, ClockIcon, DownloadIcon, FileSpreadsheetIcon, UserIcon, LockIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Doctor, DoctorSchedule } from '../../types';
import { formatTime, getStatusName } from '../../utils/helpers';
import { exportDoctorsToExcel } from '../../utils/excelExport';
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_NAMES: Record<string, string> = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo'
};
export function Doctors() {
  const {
    doctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    specialties,
    getSpecialty,
    getAppointmentsByDoctor,
    getPatient,
    getService
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('doctors');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    specialtyId: '',
    phone: '',
    email: ''
  });
  const [scheduleData, setScheduleData] = useState<DoctorSchedule>({
    monday: {
      start: '08:00',
      end: '17:00'
    },
    tuesday: {
      start: '08:00',
      end: '17:00'
    },
    wednesday: {
      start: '08:00',
      end: '17:00'
    },
    thursday: {
      start: '08:00',
      end: '17:00'
    },
    friday: {
      start: '08:00',
      end: '14:00'
    },
    saturday: null,
    sunday: null
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredDoctors = doctors.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase()) || d.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = !filterSpecialty || d.specialtyId === filterSpecialty;
    return matchesSearch && matchesSpecialty;
  });
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }, ...specialties.map(s => ({
    value: s.id,
    label: s.name
  }))];
  const specialtyOptionsForForm = specialties.map(s => ({
    value: s.id,
    label: s.name
  }));
  const openNewForm = () => {
    if (!canCreate('doctors')) return;
    setSelectedDoctor(null);
    setFormData({
      name: '',
      specialtyId: '',
      phone: '',
      email: ''
    });
    setScheduleData({
      monday: {
        start: '08:00',
        end: '17:00'
      },
      tuesday: {
        start: '08:00',
        end: '17:00'
      },
      wednesday: {
        start: '08:00',
        end: '17:00'
      },
      thursday: {
        start: '08:00',
        end: '17:00'
      },
      friday: {
        start: '08:00',
        end: '14:00'
      },
      saturday: null,
      sunday: null
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (doctor: Doctor) => {
    if (!canEdit('doctors')) return;
    setSelectedDoctor(doctor);
    setFormData({
      name: doctor.name,
      specialtyId: doctor.specialtyId,
      phone: doctor.phone,
      email: doctor.email
    });
    setScheduleData(doctor.schedule);
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openView = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.specialtyId) errors.specialtyId = 'La especialidad es requerida';
    if (!formData.phone) errors.phone = 'El teléfono es requerido';
    if (!formData.email) errors.email = 'El correo es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedDoctor) {
      updateDoctor(selectedDoctor.id, {
        ...formData,
        schedule: scheduleData
      });
    } else {
      addDoctor({
        ...formData,
        schedule: scheduleData
      });
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('doctors')) return;
    if (selectedDoctor) {
      deleteDoctor(selectedDoctor.id);
      setIsDeleteConfirmOpen(false);
      setIsViewOpen(false);
    }
  };
  const toggleDaySchedule = (day: keyof DoctorSchedule) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? null : {
        start: '08:00',
        end: '17:00'
      }
    }));
  };
  const updateDaySchedule = (day: keyof DoctorSchedule, field: 'start' | 'end', value: string) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? {
        ...prev[day]!,
        [field]: value
      } : null
    }));
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <ClockIcon className="w-4 h-4" data-id="element-748" />
  }, {
    id: 'schedule',
    label: 'Horario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-749" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-750" />
  }];
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportDoctorsToExcel(doctors, (specialtyId: string) => {
      const specialty = getSpecialty(specialtyId);
      return specialty?.name || '-';
    });
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-751">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-752">
        <div data-id="element-753">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-754">
            Doctores
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-755">
            Gestión de doctores y especialidades
            {readOnly && <span className="ml-2 text-amber-600 dark:text-amber-400" data-id="element-756">
                (Solo lectura)
              </span>}
          </p>
        </div>
        {canCreate('doctors') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-758" />} onClick={openNewForm} data-id="element-757">
            Nuevo Doctor
          </Button>}
      </div>

      {/* Beautiful Export Card */}
      <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border-purple-200 dark:border-purple-800" data-id="element-759">
        <div className="flex items-center justify-between" data-id="element-760">
          <div className="flex items-center gap-4" data-id="element-761">
            <div className="w-14 h-14 rounded-xl bg-purple-500 dark:bg-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/20" data-id="element-762">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-763" />
            </div>
            <div data-id="element-764">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-765">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-766">
                Descarga todos los doctores en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-767">
                <div className="flex items-center gap-1.5 text-sm text-purple-700 dark:text-purple-400" data-id="element-768">
                  <UserIcon className="w-4 h-4" data-id="element-769" />
                  <span className="font-medium" data-id="element-770">{doctors.length} doctores</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-771">
                  • Incluye: Nombre, Especialidad, Contacto, Horarios
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-773" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-200" data-id="element-772">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Filters */}
      <Card data-id="element-774">
        <div className="flex items-center gap-4" data-id="element-775">
          <div className="flex-1 relative" data-id="element-776">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-777" />
            <Input placeholder="Buscar por nombre o correo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-778" />
          </div>
          <div className="w-64" data-id="element-779">
            <Select options={specialtyOptions} value={filterSpecialty} onChange={setFilterSpecialty} placeholder="Filtrar por especialidad" data-id="element-780" />
          </div>
        </div>
      </Card>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-781">
          <LockIcon className="w-4 h-4" data-id="element-782" />
          <span data-id="element-783">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            doctores.
          </span>
        </div>}

      {/* Doctors List */}
      <Card padding="none" data-id="element-784">
        <div className="overflow-x-auto" data-id="element-785">
          <table className="w-full" data-id="element-786">
            <thead data-id="element-787">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-788">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-789">
                  Doctor
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-790">
                  Especialidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-791">
                  Contacto
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-792">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-793">
              {filteredDoctors.length === 0 ? <tr data-id="element-794">
                  <td colSpan={4} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-795">
                    No se encontraron doctores
                  </td>
                </tr> : filteredDoctors.map(doctor => {
              const specialty = getSpecialty(doctor.specialtyId);
              return <tr key={doctor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-796">
                      <td className="py-4 px-6" data-id="element-797">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-798">
                          Dr. {doctor.name}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-799">
                        <Badge variant="primary" data-id="element-800">
                          {specialty?.name || '-'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6" data-id="element-801">
                        <div className="space-y-1" data-id="element-802">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1" data-id="element-803">
                            <PhoneIcon className="w-3 h-3" data-id="element-804" /> {doctor.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-805">
                            <MailIcon className="w-3 h-3" data-id="element-806" /> {doctor.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-807">
                        <div className="flex items-center justify-end gap-2" data-id="element-808">
                          <button onClick={() => openView(doctor)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-809">
                            <EyeIcon className="w-4 h-4" data-id="element-810" />
                          </button>
                          {canEdit('doctors') ? <button onClick={() => openEditForm(doctor)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-811">
                              <EditIcon className="w-4 h-4" data-id="element-812" />
                            </button> : <button disabled className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Sin permisos para editar" data-id="element-813">
                              <LockIcon className="w-4 h-4" data-id="element-814" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Doctor Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedDoctor ? 'Editar Doctor' : 'Nuevo Doctor'} size="lg" data-id="element-815">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-816">
          <div className="grid grid-cols-2 gap-4" data-id="element-817">
            <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} error={formErrors.name} data-id="element-818" />
            <Select label="Especialidad" options={specialtyOptionsForForm} value={formData.specialtyId} onChange={value => setFormData({
            ...formData,
            specialtyId: value
          })} error={formErrors.specialtyId} data-id="element-819" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-820">
            <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} error={formErrors.phone} data-id="element-821" />
            <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} error={formErrors.email} data-id="element-822" />
          </div>

          {/* Schedule */}
          <div data-id="element-823">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-824">
              Horario de Atención
            </h4>
            <div className="space-y-2" data-id="element-825">
              {DAYS.map(day => <div key={day} className="flex items-center gap-4" data-id="element-826">
                  <label className="flex items-center gap-2 w-32" data-id="element-827">
                    <input type="checkbox" checked={scheduleData[day] !== null} onChange={() => toggleDaySchedule(day)} className="rounded border-gray-300 text-sky-500 focus:ring-sky-500" data-id="element-828" />
                    <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-829">
                      {DAY_NAMES[day]}
                    </span>
                  </label>
                  {scheduleData[day] && <div className="flex items-center gap-2" data-id="element-830">
                      <Input type="time" value={scheduleData[day]!.start} onChange={e => updateDaySchedule(day, 'start', e.target.value)} className="w-32" data-id="element-831" />
                      <span className="text-gray-500" data-id="element-832">a</span>
                      <Input type="time" value={scheduleData[day]!.end} onChange={e => updateDaySchedule(day, 'end', e.target.value)} className="w-32" data-id="element-833" />
                    </div>}
                </div>)}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4" data-id="element-834">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-835">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-836">
              {selectedDoctor ? 'Guardar Cambios' : 'Crear Doctor'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Doctor View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedDoctor ? `Dr. ${selectedDoctor.name}` : 'Doctor'} size="lg" data-id="element-837">
        {selectedDoctor && <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-838">
            <TabPanel id="info" activeTab={activeTab} data-id="element-839">
              <div className="space-y-6" data-id="element-840">
                <div className="grid grid-cols-2 gap-6" data-id="element-841">
                  <div data-id="element-842">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-843">
                      Especialidad
                    </h4>
                    <p className="text-gray-900 dark:text-white" data-id="element-844">
                      {getSpecialty(selectedDoctor.specialtyId)?.name}
                    </p>
                  </div>
                  <div data-id="element-845">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-846">
                      Teléfono
                    </h4>
                    <p className="text-gray-900 dark:text-white flex items-center gap-2" data-id="element-847">
                      <PhoneIcon className="w-4 h-4" data-id="element-848" /> {selectedDoctor.phone}
                    </p>
                  </div>
                  <div data-id="element-849">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-850">
                      Correo
                    </h4>
                    <p className="text-gray-900 dark:text-white flex items-center gap-2" data-id="element-851">
                      <MailIcon className="w-4 h-4" data-id="element-852" /> {selectedDoctor.email}
                    </p>
                  </div>
                </div>

                {!readOnly && <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-853">
                    {canEdit('doctors') && <Button variant="outline" onClick={() => openEditForm(selectedDoctor)} data-id="element-854">
                        <EditIcon className="w-4 h-4 mr-2" data-id="element-855" /> Editar
                      </Button>}
                    {canDelete('doctors') && <Button variant="danger" onClick={() => setIsDeleteConfirmOpen(true)} data-id="element-856">
                        <TrashIcon className="w-4 h-4 mr-2" data-id="element-857" /> Eliminar
                      </Button>}
                  </div>}
              </div>
            </TabPanel>

            <TabPanel id="schedule" activeTab={activeTab} data-id="element-858">
              <div className="space-y-3" data-id="element-859">
                {DAYS.map(day => <div key={day} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-860">
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-861">
                      {DAY_NAMES[day]}
                    </span>
                    {selectedDoctor.schedule[day] ? <span className="text-gray-600 dark:text-gray-400" data-id="element-862">
                        {formatTime(selectedDoctor.schedule[day]!.start)} -{' '}
                        {formatTime(selectedDoctor.schedule[day]!.end)}
                      </span> : <span className="text-gray-400" data-id="element-863">No disponible</span>}
                  </div>)}
              </div>
            </TabPanel>

            <TabPanel id="appointments" activeTab={activeTab} data-id="element-864">
              <div className="space-y-4" data-id="element-865">
                {getAppointmentsByDoctor(selectedDoctor.id).length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-866">
                    No hay citas registradas
                  </p> : getAppointmentsByDoctor(selectedDoctor.id).map(apt => {
              const patient = getPatient(apt.patientId);
              const service = getService(apt.serviceId);
              return <div key={apt.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-867">
                        <div data-id="element-868">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-869">
                            {patient?.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-870">
                            {apt.date} - {formatTime(apt.time)} •{' '}
                            {service?.name}
                          </p>
                        </div>
                        <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-871">
                          {getStatusName(apt.status)}
                        </Badge>
                      </div>;
            })}
              </div>
            </TabPanel>
          </Tabs>}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-872">
        <div className="space-y-4" data-id="element-873">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-874">
            ¿Está seguro de que desea eliminar a este doctor? Esta acción no se
            puede deshacer.
          </p>
          <div className="flex justify-end gap-3" data-id="element-875">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-876">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-877">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}